import React, { useMemo, useState } from 'react';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';
import ShieldIcon from '@mui/icons-material/Shield';
import {
  ExportActivity,
  ScreeningResult,
  ExportProduct,
  ExportClassificationResult,
  ExportLicense,
  ProductEntityLink,
} from 'api/ExportHubAPI';
import { Severity, sevPillClass, sevBoxClass } from './productDetailShell';

/**
 * Product 360 — the expanded product history (spec §5).
 *
 * Built entirely from REAL events: product creation, the ExportActivity log,
 * screening runs, the latest ITAR/EAR classification findings, linked
 * licenses/entities, an active coverage gap, and the current compliance score.
 * It does NOT synthesize "because X → Y" causal chains or fabricate follow-on
 * events at invented timestamps — every row corresponds to something that
 * actually happened, at its real time.
 */
export type ActivitySection =
  | 'classification'
  | 'jurisdiction'
  | 'screening'
  | 'licenses'
  | 'entities'
  | 'compliance'
  | 'documents';

export interface Activity360Event {
  id: string;
  at: string;
  section: ActivitySection;
  tone: Severity;
  title: string;
  body?: string;
  actor: string;
  reason?: string;
  from?: string;
  to?: string;
  details?: { label: string; value: string }[];
}

// Sections that have an on-page anchor to jump to (spec §5: links to section).
const SECTION_ANCHOR: Partial<Record<ActivitySection, string>> = {
  classification: 'classification',
  jurisdiction: 'jurisdiction',
  screening: 'screening',
  licenses: 'licenses',
  entities: 'entities',
};

export const SECTION_META: Record<
  ActivitySection,
  { label: string; tone: Severity; icon: React.ElementType }
> = {
  classification: { label: 'Classification', tone: 'info', icon: DescriptionIcon },
  jurisdiction: { label: 'Export Control Regime', tone: 'info', icon: VerifiedUserIcon },
  screening: { label: 'Screening', tone: 'warning', icon: SearchIcon },
  licenses: { label: 'Licenses', tone: 'success', icon: ArticleIcon },
  entities: { label: 'Entities', tone: 'info', icon: BusinessIcon },
  compliance: { label: 'Compliance/Risk', tone: 'warning', icon: ShieldIcon },
  documents: { label: 'Documents', tone: 'info', icon: DescriptionIcon },
};

const CONTROLLING = ['flagged', 'needs_review', 'license_required'];

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

// Map a raw ExportActivity onto a section for tagging.
const sectionForActivity = (a: ExportActivity): ActivitySection => {
  const s = `${a.action} ${a.message}`.toLowerCase();
  if (s.includes('license')) return 'licenses';
  if (s.includes('jurisdiction') || s.includes('itar') || s.includes('eccn'))
    return 'jurisdiction';
  if (s.includes('entity') || s.includes('customer') || s.includes('consignee'))
    return 'entities';
  if (s.includes('screen') || s.includes('classif') || s.includes('check'))
    return 'screening';
  if (s.includes('score') || s.includes('risk')) return 'compliance';
  if (s.includes('document') || s.includes('upload')) return 'documents';
  return 'classification';
};

const toneForRisk = (risk: string | null): Severity => {
  if (!risk) return 'info';
  const r = risk.toLowerCase();
  if (r === 'critical') return 'danger';
  if (r === 'high') return 'warning';
  if (r === 'medium') return 'info';
  return 'success';
};

interface BuildArgs {
  product: ExportProduct;
  activity: ExportActivity[];
  screenings: ScreeningResult[];
  classifications: ExportClassificationResult[];
  licenses: ExportLicense[];
  entities: ProductEntityLink[];
  coverageGap: string[];
  score: number;
}

function buildEvents(a: BuildArgs): Activity360Event[] {
  const {
    product,
    activity,
    screenings,
    classifications,
    licenses,
    entities,
    coverageGap,
    score,
  } = a;
  const events: Activity360Event[] = [];
  const latest: Partial<Record<string, ExportClassificationResult>> = {};
  classifications.forEach((c) => {
    if (!latest[c.regime]) latest[c.regime] = c;
  });

  // ── Real events ──────────────────────────────────────────────────────
  events.push({
    id: 'created',
    at: product.created_at,
    section: 'classification',
    tone: 'info',
    title: 'Product record created',
    actor: product.created_by_name || 'System',
    reason: 'Product onboarding.',
  });
  activity.forEach((ev) => {
    events.push({
      id: `act-${ev.id}`,
      at: ev.created_at,
      section: sectionForActivity(ev),
      tone: 'info',
      title: ev.message,
      actor: ev.actor_name || 'System',
    });
  });
  screenings.forEach((s) => {
    events.push({
      id: `scr-${s.id}`,
      at: s.created_at,
      section: 'screening',
      tone: toneForRisk(s.risk),
      title: `${s.check_type.replace(/_/g, ' ')} run`,
      body: s.summary || undefined,
      actor: s.created_by_name || 'System',
      details: s.risk ? [{ label: 'Risk', value: s.risk }] : undefined,
    });
  });

  // ── Latest classification findings (real results from the classifier) ──
  // These surface the product's most recent ITAR / EAR determinations at their
  // real timestamps. No synthesized follow-on events or causal links.
  const itar = latest.itar;
  if (itar && CONTROLLING.includes(itar.status)) {
    const cat =
      str((itar.result || {}).usml_category) ||
      str((product.jurisdiction_details || {}).usml_category) ||
      'a USML category';
    events.push({
      id: 'itar-run',
      at: itar.created_at,
      section: 'screening',
      tone: 'warning',
      title: `ITAR Check — USML ${cat} identified`,
      actor: 'System',
      details: [{ label: 'USML Category', value: cat }],
    });
  }

  const ear = latest.ear_ccl;
  if (ear && ear.eccn) {
    events.push({
      id: 'ear-run',
      at: ear.created_at,
      section: 'screening',
      tone: 'info',
      title: `EAR/CCL Check — candidate ECCN ${ear.eccn}`,
      actor: 'System',
      details: [{ label: 'Primary ECCN', value: ear.eccn }],
    });
  }

  // Linked entities drive effective destinations.
  entities.forEach((e) => {
    if (!e.country) return;
    events.push({
      id: `ent-${e.id}`,
      at: e.linked_at,
      section: 'entities',
      tone: 'info',
      title: `${e.role} ${e.name} linked`,
      actor: 'System',
      reason: `Added ${e.country} to the product's effective destinations.`,
    });
  });

  // Linked licenses (+ expiry).
  licenses.forEach((l) => {
    const label = l.license_number || l.license_type;
    events.push({
      id: `lic-${l.id}`,
      at: l.created_at,
      section: 'licenses',
      tone: 'success',
      title: `License linked: ${label}`,
      actor: l.created_by_name || 'System',
      details: [
        { label: 'Type', value: l.license_type },
        { label: 'Agency', value: l.agency },
      ],
    });
    if (l.status === 'Expired') {
      events.push({
        id: `lic-exp-${l.id}`,
        at: l.expiry_date || l.last_updated,
        section: 'licenses',
        tone: 'warning',
        title: `License ${label} expired`,
        actor: 'System',
        reason: 'Product now has one fewer active license.',
      });
    }
  });

  // Coverage gap raised (linked to the entity that introduced the destination).
  if (coverageGap.length) {
    const cause = entities.find((e) => coverageGap.includes(e.country || ''));
    events.push({
      id: 'cov-gap',
      at: product.last_updated,
      section: 'licenses',
      tone: 'warning',
      title: `Coverage gap raised — ${coverageGap.join(
        ', ',
      )} not covered by linked licenses`,
      actor: 'System',
      reason: cause
        ? `Destination added via ${cause.role.toLowerCase()} ${cause.name}.`
        : 'Effective destinations exceed linked-license coverage.',
    });
  }

  // Compliance score (current value; no historical prior available).
  const needsJur =
    product.jurisdiction === 'ITAR' &&
    !str((product.jurisdiction_details || {}).usml_category);
  events.push({
    id: 'score',
    at: product.last_updated,
    section: 'compliance',
    tone: score >= 90 ? 'success' : score >= 70 ? 'warning' : 'danger',
    title: `Compliance score is ${score}/100`,
    actor: 'System',
    reason: needsJur
      ? 'Jurisdiction detail incomplete for ITAR.'
      : 'Recomputed from the latest classification and licensing state.',
  });

  // Dedup on section+title, keeping the richer event on collision.
  const richness = (e: Activity360Event) =>
    (e.from || e.to ? 1 : 0) + (e.details ? 1 : 0) + (e.reason ? 1 : 0);
  const byKey = new Map<string, Activity360Event>();
  events.forEach((e) => {
    const key = `${e.section}::${e.title.toLowerCase().replace(/\s+/g, ' ').trim()}`;
    const prev = byKey.get(key);
    if (!prev || richness(e) > richness(prev)) byKey.set(key, e);
  });

  return [...byKey.values()].sort(
    (x, y) => +new Date(y.at) - +new Date(x.at),
  );
}

const TimelineList: React.FC<{
  events: Activity360Event[];
  onJumpToSection?: (section: ActivitySection) => void;
}> = ({ events, onJumpToSection }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  return (
    <ol className='eh-timeline'>
      {events.map((event) => {
        const meta = SECTION_META[event.section];
        const Icon = meta.icon;
        const isOpen = !!expanded[event.id];
        const anchor = SECTION_ANCHOR[event.section];
        return (
          <li key={event.id} className='eh-timeline-item'>
            <span className={`eh-timeline-dot ${sevBoxClass(event.tone)}`}>
              <Icon style={{ fontSize: 14 }} />
            </span>
            <button
              type='button'
              className='eh-timeline-row'
              onClick={() =>
                setExpanded((s) => ({ ...s, [event.id]: !s[event.id] }))
              }
            >
              <div className='eh-timeline-main'>
                <div className='eh-timeline-titleline'>
                  <span className='eh-timeline-title'>{event.title}</span>
                  <span className={sevPillClass(meta.tone)}>{meta.label}</span>
                </div>
                {event.body && <p className='eh-timeline-body'>{event.body}</p>}
                <div className='eh-timeline-meta'>
                  <span>By {event.actor}</span>
                  {event.reason && <span>· {event.reason}</span>}
                </div>
              </div>
              <div className='eh-timeline-side'>
                <time>{moment(event.at).format('MM/DD/YYYY')}</time>
                <ExpandMoreIcon
                  style={{
                    fontSize: 16,
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              </div>
            </button>
            {isOpen && (
              <div className='eh-timeline-detail eh-soft-box'>
                {(event.from || event.to) && (
                  <div className='eh-timeline-change'>
                    <span className='eh-drawer-eyebrow'>Change</span>
                    <span className='eh-mono'>{event.from ?? '—'}</span>
                    <span>→</span>
                    <span className='eh-mono eh-timeline-to'>
                      {event.to ?? '—'}
                    </span>
                  </div>
                )}
                {event.details && (
                  <div className='eh-timeline-details-grid'>
                    {event.details.map((d) => (
                      <div key={d.label}>
                        <span className='eh-drawer-eyebrow'>{d.label}</span>
                        <span className='eh-timeline-detail-val'>{d.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className='eh-timeline-detail-foot'>
                  <span className='eh-timeline-ts'>
                    {moment(event.at).format('MMM D, YYYY · h:mm A')}
                  </span>
                  {anchor && onJumpToSection && (
                    <button
                      type='button'
                      className='eh-timeline-jump'
                      onClick={(e) => {
                        e.stopPropagation();
                        onJumpToSection(event.section);
                      }}
                    >
                      View in {meta.label} <NorthEastIcon style={{ fontSize: 12 }} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const SECTION_KEYS = Object.keys(SECTION_META) as ActivitySection[];

interface Props extends BuildArgs {
  onJumpToSection?: (section: ActivitySection) => void;
}

const ProductActivity360: React.FC<Props> = (props) => {
  const { onJumpToSection } = props;
  const events = useMemo(
    () => buildEvents(props),
    [
      props.product,
      props.activity,
      props.screenings,
      props.classifications,
      props.licenses,
      props.entities,
      props.coverageGap,
      props.score,
    ],
  );
  const [filter, setFilter] = useState<ActivitySection | 'all'>('all');
  const [query, setQuery] = useState('');
  const [groupMode, setGroupMode] = useState<'chronological' | 'section'>(
    'chronological',
  );
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filtered = events.filter((e) => {
    if (filter !== 'all' && e.section !== filter) return false;
    if (query.trim()) {
      const hay =
        `${e.title} ${e.body ?? ''} ${e.actor} ${e.reason ?? ''} ${e.from ?? ''} ${e.to ?? ''}`.toLowerCase();
      if (!hay.includes(query.toLowerCase())) return false;
    }
    const day = moment(e.at);
    if (dateFrom && day.isBefore(moment(dateFrom), 'day')) return false;
    if (dateTo && day.isAfter(moment(dateTo), 'day')) return false;
    return true;
  });

  const grouped: [ActivitySection, Activity360Event[]][] =
    groupMode === 'section'
      ? SECTION_KEYS.map(
          (s) =>
            [s, filtered.filter((e) => e.section === s)] as [
              ActivitySection,
              Activity360Event[],
            ],
        ).filter(([, l]) => l.length > 0)
      : [];

  const chips: (ActivitySection | 'all')[] = ['all', ...SECTION_KEYS];

  return (
    <section className='eh-360'>
      <div>
        <h2 className='eh-pd-section-title'>Product 360 history</h2>
        <p className='eh-pd-section-sub'>
          Everything that has happened to this product — classifications,
          jurisdiction determinations, screenings, licenses, entities, and
          compliance decisions.
        </p>
      </div>
      <div className='eh-panel eh-360-panel'>
        <div className='eh-360-toolbar'>
          <div className='export-hub-search eh-360-search'>
            <SearchIcon className='export-hub-search-icon' style={{ fontSize: 16 }} />
            <input
              className='export-hub-search-input'
              placeholder='Search events, codes, users, licenses…'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div className='eh-360-toggle'>
            <button
              type='button'
              className={groupMode === 'chronological' ? 'active' : undefined}
              onClick={() => setGroupMode('chronological')}
            >
              Chronological
            </button>
            <button
              type='button'
              className={groupMode === 'section' ? 'active' : undefined}
              onClick={() => setGroupMode('section')}
            >
              By section
            </button>
          </div>
        </div>
        <div className='eh-360-daterow'>
          <div className='eh-360-chips'>
            {chips.map((s) => {
              const label = s === 'all' ? 'All' : SECTION_META[s].label;
              const count =
                s === 'all'
                  ? events.length
                  : events.filter((e) => e.section === s).length;
              return (
                <button
                  key={s}
                  type='button'
                  className={`eh-360-chip${filter === s ? ' active' : ''}`}
                  onClick={() => setFilter(s)}
                >
                  {label} <span className='eh-360-chip-count'>{count}</span>
                </button>
              );
            })}
          </div>
          <div className='eh-360-dates'>
            <input
              type='date'
              className='eh-input eh-360-date'
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label='From date'
            />
            <span className='eh-360-date-sep'>→</span>
            <input
              type='date'
              className='eh-input eh-360-date'
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label='To date'
            />
            {(dateFrom || dateTo) && (
              <button
                type='button'
                className='eh-mini-btn'
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className='eh-360-empty'>No events match your filters.</div>
        ) : groupMode === 'section' ? (
          <div className='eh-360-groups'>
            {grouped.map(([sec, list]) => (
              <div key={sec}>
                <div className='eh-360-group-head'>
                  <span className={sevPillClass(SECTION_META[sec].tone)}>
                    {SECTION_META[sec].label}
                  </span>
                  <span className='eh-360-group-count'>
                    {list.length} event{list.length === 1 ? '' : 's'}
                  </span>
                </div>
                <TimelineList
                  events={list}
                  onJumpToSection={onJumpToSection}
                />
              </div>
            ))}
          </div>
        ) : (
          <TimelineList events={filtered} onJumpToSection={onJumpToSection} />
        )}
      </div>
    </section>
  );
};

export default ProductActivity360;
